/**
 * P60 Service
 * Handles P60 document generation and retrieval
 */

import bcClient from '../businessCentralClient';
import { BC_CONFIG } from '../config';

/**
 * Get closed fiscal years
 * @returns {Promise<Array>} List of closed fiscal years
 */
export async function getClosedFiscalYears() {
  try {
    console.log('📅 Fetching closed fiscal years');

    // Get BC token
    const tokenData = await bcClient.getToken();
    const token = tokenData.access_token;

    // Construct the closed fiscal years endpoint
    const endpoint = `/v2.0/${BC_CONFIG.tenantId}/${BC_CONFIG.environment}/api/dogma/payroll/v2.0/companies(${BC_CONFIG.companyId})/closedFiscalYears`;

    console.log('Closed Fiscal Years Endpoint:', endpoint);

    // Call the endpoint
    const response = await bcClient.get(endpoint, token);

    console.log('✅ Closed fiscal years retrieved successfully');

    // The response should contain the fiscal years in a 'value' array
    if (response && response.value) {
      return response.value;
    } else if (Array.isArray(response)) {
      return response;
    } else {
      throw new Error('Invalid response format from closed fiscal years endpoint');
    }
  } catch (error) {
    console.error('❌ Error fetching closed fiscal years:', error);
    throw error;
  }
}

/**
 * Generate P60 document in base64 format
 * @param {string} employeeNo - Employee number
 * @param {string} fiscalYear - Fiscal year (e.g., "2025/2026")
 * @returns {Promise<string>} Base64 encoded PDF document
 */
export async function generateP60Document(employeeNo, fiscalYear) {
  try {
    console.log(`📄 Generating P60 for employee: ${employeeNo}, fiscal year: ${fiscalYear}`);

    // Get BC token
    const tokenData = await bcClient.getToken();
    const token = tokenData.access_token;

    // Construct the P60 generator endpoint
    const endpoint = `/v2.0/${BC_CONFIG.tenantId}/${BC_CONFIG.environment}/api/dogma/payroll/v2.0/companies(${BC_CONFIG.companyId})/p60Generators('${employeeNo}')/Microsoft.NAV.GenerateP60Base64`;

    console.log('P60 Endpoint:', endpoint);

    // Request body with fiscal year
    const requestBody = {
      fiscalYear: fiscalYear
    };

    console.log('Request body:', requestBody);

    // Call the P60 generation endpoint
    const response = await bcClient.post(endpoint, token, requestBody);

    console.log('✅ P60 generated successfully');

    // The response should contain the base64 PDF
    // Business Central API typically returns the result in a 'value' property
    if (response && response.value) {
      return response.value;
    } else if (typeof response === 'string') {
      return response;
    } else {
      throw new Error('Invalid response format from P60 generation endpoint');
    }
  } catch (error) {
    console.error('❌ Error generating P60:', error);
    throw error;
  }
}

/**
 * Download P60 document
 * @param {string} base64Data - Base64 encoded PDF
 * @param {string} employeeNo - Employee number for filename
 * @param {string} fiscalYear - Fiscal year for filename
 */
export function downloadP60Document(base64Data, employeeNo, fiscalYear) {
  try {
    // Convert base64 to blob
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // Replace / with - for filename safety
    const safeFiscalYear = fiscalYear.replace(/\//g, '-');
    link.download = `P60_${employeeNo}_${safeFiscalYear}_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('✅ P60 document downloaded');
  } catch (error) {
    console.error('❌ Error downloading P60:', error);
    throw error;
  }
}

const p60Service = {
  getClosedFiscalYears,
  generateP60Document,
  downloadP60Document,
};

export default p60Service;
